import type { Citation } from './types';
import type { LedningsrapportUnderlag } from '../../netlify/functions/_shared/ledningsrapport/typer';
import type { Kalla, Mote, MotesTyp, Status as MotesStatus } from '../../netlify/functions/_shared/moten/typer';
import { supabase } from './supabase';

async function authHeader(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Du är inte inloggad — logga in igen.');
  return `Bearer ${token}`;
}

export interface AISearchRequest {
  query: string;
  conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>;
  attachedFileContent?: string | null;
}

export interface AISearchResponse {
  answer: string;
  citations: Citation[];
  sourceFiles: string[];
  grounded: boolean;
  suggestedFollowUps?: string[];
}

/**
 * Läsbart felmeddelande ur ett misslyckat svar.
 *
 * Tidigare interpolerades svarskroppen rakt in i felet. När gatewayen svarade
 * 504 var kroppen en HTML-sida, och hela den sidan hamnade i chattbubblan:
 *
 *   Ett fel inträffade: AI-sökning misslyckades (504): <HTML> <HEAD>
 *   <TITLE>Inactivity Timeout</TITLE> </HEAD> <BODY BGCOLOR="white" …
 *
 * Det är precis vad Linnea rapporterade 2026-06-12. Själva timeouten är löst
 * sedan svaren strömmas (v1.13.0, samma dag), men felVISNINGEN var kvar.
 *
 * STATUSEN AVGÖR FÖRST, och det är avsiktligt. Att bara tolka JSON räcker inte:
 * ai-search.ts fångar ALLT i ett yttre catch och returnerar
 * `{ error: (e as Error).message }` med 502. Kroppen är alltså giltig JSON även
 * när felet är internt, och en överbelastad modell hade visats som
 * `Sökningen misslyckades: 529 {"type":"error","error":{...}}` — samma läcka som
 * HTML-dumpen, bara JSON-formad. Serverns egen feltext är skriven för loggar,
 * inte för en chattbubbla.
 *
 * Diagnostiken går inte förlorad: ai-search loggar `errorMessage` till
 * ai_unanswered via logQuestionOutcome, så det interna felet finns kvar där det
 * hör hemma.
 */
async function felMeddelande(res: Response, vad: string): Promise<string> {
  // Sessionen har löpt ut. Serverns text är "Invalid or expired session" —
  // engelska, och utan den enda åtgärd som hjälper.
  if (res.status === 401 || res.status === 403) {
    return 'Din inloggning har gått ut. Ladda om sidan och logga in igen.';
  }

  if (res.status === 408 || res.status === 504) {
    return `${vad} tog för lång tid och avbröts. Ställ gärna frågan igen, eller dela upp den i två kortare frågor.`;
  }

  // Alla 5xx: visa aldrig serverns interna text.
  if (res.status >= 500) {
    return `${vad} kunde inte slutföras just nu (${res.status}). Försök igen om en stund — felet är loggat.`;
  }

  // Kvar: 4xx. Där är serverns meddelande avsett för den som frågar
  // (valideringsfel och liknande) och hjälper faktiskt.
  let detalj = '';
  // text() först — json() förbrukar kroppen och går inte att läsa om när den
  // inte är JSON, vilket är exakt fallet vi försöker hantera.
  try {
    const raa = await res.text();
    const tolkad = JSON.parse(raa) as { error?: string };
    if (tolkad?.error) {
      const hel = String(tolkad.error);
      // Ellips vid kapning, annars läser ett avhugget fragment som en
      // fullständig mening när punkten läggs på.
      detalj = hel.length > 300 ? `${hel.slice(0, 300)}…` : hel;
    }
  } catch {
    /* inte JSON — kroppen är då en felsida och ska inte visas */
  }

  return detalj ? `${vad} misslyckades: ${detalj}` : `${vad} misslyckades (HTTP ${res.status}).`;
}

export async function aiSearch(req: AISearchRequest): Promise<AISearchResponse> {
  const res = await fetch('/api/ai-search', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await authHeader(),
    },
    body: JSON.stringify(req),
  });

  if (!res.ok) {
    throw new Error(await felMeddelande(res, 'Sökningen'));
  }

  /*
   * Ett 200-svar är inte samma sak som ett användbart svar.
   *
   * `res.json()` kastar ett tekniskt SyntaxError ("Unexpected token < in JSON
   * at position 0") om kroppen inte är JSON — och ChatContext hade visat den
   * engelska texten i chattbubblan. Det är precis samma sorts läckage som den
   * här ändringen stänger på felvägen, bara via lyckad-vägen.
   *
   * Formen kontrolleras också: ett JSON-svar med fel form passerar typningen
   * och hade skapat en tom assistentbubbla utan att något såg fel ut.
   */
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw new Error('Svaret från ELvis kunde inte tolkas. Försök igen om en stund.');
  }
  const svar = data as Partial<AISearchResponse> | null;
  if (!svar || typeof svar.answer !== 'string') {
    throw new Error('Svaret från ELvis var ofullständigt. Försök igen om en stund.');
  }

  return {
    answer: svar.answer,
    citations: svar.citations ?? [],
    sourceFiles: svar.sourceFiles ?? [],
    grounded: svar.grounded ?? false,
    suggestedFollowUps: svar.suggestedFollowUps ?? [],
  };
}

export interface AISearchStreamMeta {
  answer: string;
  citations: Citation[];
  sourceFiles: string[];
  grounded: boolean;
  suggestedFollowUps: string[];
}

export interface AISearchStreamCallbacks {
  /** Anropas löpande med ny synlig text (token-för-token). */
  onDelta: (text: string) => void;
  /** Anropas en gång när svaret är klart, med citations/källor/följdfrågor. */
  onDone: (meta: AISearchStreamMeta) => void;
}

/** Strömmad variant av aiSearch (Vy #2). Läser NDJSON-strömmen rad för rad och
 *  matar onDelta/onDone. Innehållet är identiskt med aiSearch — bara levererat
 *  i takt med att ELvis skriver. */
export async function aiSearchStream(
  req: AISearchRequest,
  cb: AISearchStreamCallbacks,
): Promise<void> {
  const res = await fetch('/api/ai-search', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await authHeader(),
    },
    body: JSON.stringify({ ...req, stream: true }),
  });

  if (!res.ok) {
    throw new Error(await felMeddelande(res, 'Sökningen'));
  }
  // Separat från !res.ok: en 200 utan kropp är inget HTTP-fel, och
  // felMeddelande hade svarat "misslyckades (HTTP 200)" — obegripligt.
  if (!res.body) {
    throw new Error(
      'Svaret kunde inte strömmas i den här webbläsaren. Ladda om sidan och försök igen.',
    );
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let done: AISearchStreamMeta | null = null;

  const handleLine = (line: string) => {
    const l = line.trim();
    if (!l) return;
    let evt: { type?: string; text?: string; message?: string } & Partial<AISearchStreamMeta>;
    try {
      evt = JSON.parse(l);
    } catch {
      return;
    }
    if (evt.type === 'delta' && typeof evt.text === 'string') {
      cb.onDelta(evt.text);
    } else if (evt.type === 'done') {
      done = {
        answer: evt.answer ?? '',
        citations: evt.citations ?? [],
        sourceFiles: evt.sourceFiles ?? [],
        grounded: evt.grounded ?? false,
        suggestedFollowUps: evt.suggestedFollowUps ?? [],
      };
    } else if (evt.type === 'error') {
      throw new Error(evt.message || 'Strömningsfel');
    }
  };

  for (;;) {
    const { value, done: streamDone } = await reader.read();
    if (streamDone) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop() ?? '';
    for (const line of lines) handleLine(line);
  }
  if (buf.trim()) handleLine(buf);

  if (!done) throw new Error('Strömmen avslutades utan komplett svar.');
  cb.onDone(done);
}

export type MailCreativity = 'saklig' | 'balanserad' | 'personlig';

export interface MailAssistantRequest {
  customerEmail: string;
  responseLanguage: 'sv' | 'en';
  creativity?: MailCreativity;
}

export interface UsedTemplate {
  id: string;
  similarity: number;
  preview: string;
}

export interface MailAssistantResponse {
  reply: string;
  summary: string;
  sourceFiles: string[];
  gaps: string[];
  language: 'sv' | 'en';
  logId: string | null;
  usedTemplates: UsedTemplate[];
}

export async function mailAssistant(
  req: MailAssistantRequest,
  token?: string,
): Promise<MailAssistantResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch('/api/mail-assistant', {
    method: 'POST',
    headers,
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

export interface MailAssistantStreamCallbacks {
  onDelta: (text: string) => void;
  onDone: (result: MailAssistantResponse) => void;
}

/**
 * Strömmande variant av mailAssistant. Mail-texten levereras token-för-token via
 * onDelta; summary/gaps/källor/logId kommer i onDone. Krävs för att undvika
 * Netlifys 26s-gateway-timeout (504) — svaret börjar flöda direkt.
 */
export async function mailAssistantStream(
  req: MailAssistantRequest,
  cb: MailAssistantStreamCallbacks,
  token?: string,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch('/api/mail-assistant', {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...req, stream: true }),
  });

  if (!res.ok || !res.body) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let done: MailAssistantResponse | null = null;

  const handleLine = (line: string) => {
    const l = line.trim();
    if (!l) return;
    let evt: { type?: string; text?: string; message?: string } & Partial<MailAssistantResponse>;
    try {
      evt = JSON.parse(l);
    } catch {
      return;
    }
    if (evt.type === 'delta' && typeof evt.text === 'string') {
      cb.onDelta(evt.text);
    } else if (evt.type === 'done') {
      done = {
        reply: evt.reply ?? '',
        summary: evt.summary ?? '',
        gaps: evt.gaps ?? [],
        sourceFiles: evt.sourceFiles ?? [],
        language: evt.language ?? req.responseLanguage,
        logId: evt.logId ?? null,
        usedTemplates: evt.usedTemplates ?? [],
      };
    } else if (evt.type === 'error') {
      throw new Error(evt.message || 'Strömningsfel');
    }
  };

  for (;;) {
    const { value, done: streamDone } = await reader.read();
    if (streamDone) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop() ?? '';
    for (const line of lines) handleLine(line);
  }
  if (buf.trim()) handleLine(buf);

  if (!done) throw new Error('Strömmen avslutades utan komplett svar.');
  cb.onDone(done);
}

export interface AnswerFeedbackRequest {
  feature: 'mail_assistant' | 'ai_search';
  reference_id?: string | null;
  rating: 'up' | 'down';
  comment?: string | null;
}

export async function submitAnswerFeedback(
  req: AnswerFeedbackRequest,
  token?: string,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch('/api/answer-feedback', {
    method: 'POST',
    headers,
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
}

export type FeedbackCategory = 'forbattring' | 'bugg' | 'fraga' | 'annat';

export async function submitFeedback(
  category: FeedbackCategory,
  message: string,
): Promise<void> {
  const res = await fetch('/api/submit-feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await authHeader() },
    body: JSON.stringify({ category, message }),
  });
  if (!res.ok) {
    let m = `HTTP ${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) m = b.error;
    } catch {
      /* ignore */
    }
    throw new Error(m);
  }
}

/* ============================================================
   Egen feedback — historiken bakom formuläret
   ============================================================ */

export type FeedbackStatus = 'ny' | 'mottagen' | 'pagar' | 'vantar_kund' | 'klar' | 'avvisad';

export interface FeedbackPost {
  id: string;
  category: FeedbackCategory;
  message: string;
  status: FeedbackStatus;
  svar: string | null;
  createdAt: string;
  besvaradAt: string | null;
  seddAvSamifyAt: string | null;
  arendenummer: string | null;
  /** Versionen av ELvis Hub där ärendet åtgärdades. Null tills en release
   *  stängt det — "Klar" säger att något gjorts, den här säger i vilken
   *  release det blev tillgängligt. */
  atgardadIVersion: string | null;
}

/**
 * Hämtar den inloggades EGNA feedback.
 *
 * Går direkt mot Supabase och inte via en funktion: RLS-policyn
 * `user_feedback egen laesning` begränsar redan till `user_id = auth.uid()`,
 * och ett extra led hade bara varit ett till ställe där filtret kan bli fel.
 *
 * ⚠️ Inget `.eq('user_id', …)` här, med flit. Avgränsningen är RLS, och ett
 * filter i klienten skyddar ingenting — den som vill åt andras rader anropar
 * inte den här funktionen. Det hade bara gett sken av två grindar där det
 * finns en. `user_id` går dessutom inte ens att läsa längre: kolumn-granten
 * i migrationen lämnar ut exakt fälten nedan.
 *
 * ⚠️ `internal_note` hämtas ALDRIG — och kan inte hämtas. Den är vår
 * anteckning om posten, inte vårt svar till den som skrev, och den är
 * formulerad därefter. RLS kan inte begränsa kolumner; GRANT gör det.
 */
export async function fetchMyFeedback(): Promise<FeedbackPost[]> {
  const { data, error } = await supabase
    .from('user_feedback')
    .select(
      'id, category, message, status, svar, created_at, besvarad_at, sedd_av_samify_at, crm_ticket_number, atgardad_i_version',
    )
    .order('created_at', { ascending: false })
    .limit(200);

  if (error) throw new Error(error.message);

  return (data ?? []).map((r) => ({
    id: r.id as string,
    category: r.category as FeedbackCategory,
    message: r.message as string,
    status: (r.status ?? 'ny') as FeedbackStatus,
    svar: (r.svar ?? null) as string | null,
    createdAt: r.created_at as string,
    besvaradAt: (r.besvarad_at ?? null) as string | null,
    seddAvSamifyAt: (r.sedd_av_samify_at ?? null) as string | null,
    arendenummer: (r.crm_ticket_number ?? null) as string | null,
    atgardadIVersion: (r.atgardad_i_version ?? null) as string | null,
  }));
}

export interface IngestUrlResponse {
  ok: true;
  source: string;
  title: string | null;
  chunks: number;
  tokens: number;
}

export async function ingestUrl(url: string): Promise<IngestUrlResponse> {
  const res = await fetch('/api/ingest-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await authHeader() },
    body: JSON.stringify({ url }),
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  return res.json();
}

export interface DiscoveredUrl {
  url: string;
  path: string;
}

export interface CrawlDiscoverResponse {
  ok: true;
  origin: string;
  host: string;
  fromSitemap: boolean;
  total: number;
  urls: DiscoveredUrl[];
}

export async function crawlDiscover(url: string, scope?: string | null): Promise<CrawlDiscoverResponse> {
  const res = await fetch('/api/crawl-discover', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await authHeader() },
    body: JSON.stringify({ url, scope: scope ?? null }),
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

export interface IngestFileResponse {
  ok: true;
  source: string;
  chunks: number;
  tokens: number;
}

export async function ingestFile(filename: string, content: string): Promise<IngestFileResponse> {
  const res = await fetch('/api/ingest-file', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await authHeader() },
    body: JSON.stringify({ filename, content }),
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

// ---------- ELvis-rättelser (användarfeedback för att träna kunskapsbasen) ----------

export type CorrectionType = 'wrong_source' | 'outdated_source' | 'missing_in_kb';

export interface SubmitCorrectionRequest {
  question: string;
  original_answer: string;
  correction_type: CorrectionType;
  cited_source?: string | null;
  suggested_source?: string | null;
  user_note?: string | null;
}

export async function submitCorrection(req: SubmitCorrectionRequest): Promise<{ id: string }> {
  const res = await fetch('/api/submit-correction', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await authHeader(),
    },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    let m = `HTTP ${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) m = b.error;
    } catch {
      /* ignore */
    }
    throw new Error(m);
  }
  return res.json();
}

export type DriftAction = 'reviewed' | 'dismissed' | 'reopen';

export async function driftAction(driftId: string, action: DriftAction): Promise<void> {
  const res = await fetch('/api/drift-action', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await authHeader(),
    },
    body: JSON.stringify({ drift_id: driftId, action }),
  });
  if (!res.ok) {
    let m = `HTTP ${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) m = b.error;
    } catch {
      /* ignore */
    }
    throw new Error(m);
  }
}

export async function correctionAction(
  correctionId: string,
  action: 'resolve' | 'ignore' | 'reopen',
): Promise<void> {
  const res = await fetch('/api/correction-action', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await authHeader(),
    },
    body: JSON.stringify({ correction_id: correctionId, action }),
  });
  if (!res.ok) {
    let m = `HTTP ${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) m = b.error;
    } catch {
      /* ignore */
    }
    throw new Error(m);
  }
}

// ---------- Mail-assistent stil-träning ----------

export interface SubmitMailTrainingRequest {
  customer_email: string;
  ai_draft?: string | null;
  correct_reply: string;
  language: 'sv' | 'en';
  user_note?: string | null;
}

export async function submitMailTraining(req: SubmitMailTrainingRequest): Promise<{ id: string }> {
  const res = await fetch('/api/submit-mail-training', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await authHeader(),
    },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    let m = `HTTP ${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) m = b.error;
    } catch {
      /* ignore */
    }
    throw new Error(m);
  }
  return res.json();
}

// ---------- Systemstatus + schemalagd crawl ----------

export interface SystemServiceStatus {
  key: string;
  label: string;
  ok: boolean;
  latency_ms: number | null;
  detail: string;
}

export interface CrawlHeartbeat {
  completed_at: string;
  started_at: string;
  total: number;
  ok: number;
  failed: number;
  skipped: number;
  chunks: number;
}

export interface SystemStatusResponse {
  checked_at: string;
  services: SystemServiceStatus[];
  facts: {
    kb: {
      source_count: number;
      chunk_count: number;
      /**
       * Källor som står i listan men saknar chunks — går inte att söka i.
       * `null` betyder att kontrollen inte kunde göras, inte att allt är bra.
       */
      empty_sources: number | null;
      last_kb_update: string | null;
    };
    crawl: {
      last_completed: CrawlHeartbeat | null;
      fallback_last_website_update: string | null;
      in_progress: { done: number; total: number } | null;
    };
    audit: { last_run: string | null; review_pending: number; scheduled: boolean };
    drift: {
      read_failed: boolean;
      last_check: string | null;
      last_completed: boolean | null;
      open: number | null;
    };
  };
}

export async function systemStatus(): Promise<SystemStatusResponse> {
  const res = await fetch('/api/system-status', {
    method: 'GET',
    headers: { Authorization: await authHeader() },
  });
  if (!res.ok) {
    let m = `HTTP ${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) m = b.error;
    } catch {
      /* ignore */
    }
    throw new Error(m);
  }
  return res.json();
}

export interface TriggerCrawlResponse {
  action?: string;
  processed?: number;
  offset?: number;
  total?: number;
  completed?: boolean;
  idle?: boolean;
}

async function postJob<T>(path: string, body: object): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await authHeader() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let m = `HTTP ${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) m = b.error;
    } catch {
      /* ignore */
    }
    throw new Error(m);
  }
  return res.json() as Promise<T>;
}

export function triggerCrawl(action: 'start' | 'advance' = 'start'): Promise<TriggerCrawlResponse> {
  return postJob<TriggerCrawlResponse>('/api/scheduled-crawl', { action });
}

export interface AuditBatchResult {
  completed: boolean;
  batch_offset_next: number;
  chunks_total: number;
  chunks_processed: number;
  pairs_checked: number;
  contradictions_found: number;
}

export function runAuditBatch(): Promise<AuditBatchResult> {
  // Ingen offset → funktionen fortsätter från sparad position
  return postJob<AuditBatchResult>('/api/kb-audit-contradictions', {});
}

export interface DriftBatchResult {
  completed: boolean;
  batch_offset_next: number;
  sources_checked: number;
  drift_found: number;
  errors: number;
}

export function runDriftBatch(): Promise<DriftBatchResult> {
  return postJob<DriftBatchResult>('/api/kb-detect-drift', {});
}

// ---------- Kunskapslucke-stängaren ----------

export interface GapDraft {
  title: string;
  draft: string;
  needs: string[];
  usedSources: string[];
  contextFound: number;
}

export function draftGap(question: string, gapsText?: string | null): Promise<GapDraft> {
  return postJob<GapDraft>('/api/close-gap', {
    action: 'draft',
    question,
    gaps_text: gapsText ?? null,
  });
}

export function commitGap(
  id: string,
  filename: string,
  content: string,
): Promise<{ ok: true; filename: string; chunks: number }> {
  return postJob('/api/close-gap', { action: 'commit', id, filename, content });
}

export function dismissGap(id: string): Promise<{ ok: true }> {
  return postJob('/api/close-gap', { action: 'dismiss', id });
}

export function resolveGap(id: string): Promise<{ ok: true }> {
  return postJob('/api/close-gap', { action: 'resolve', id });
}

// ---------- Luck-triagen ----------

/**
 * Facken en lucka kan hamna i.
 *
 * `osorterad` och `obedomd` är INTE samma sak, och skillnaden är hela poängen:
 *  - `osorterad` = klassningen har ännu inte körts för den här raden (t.ex. medan
 *    listan visas rått ur databasen i väntan på svar). Ett påstående om att den
 *    inte gick att bedöma vore falskt.
 *  - `obedomd`  = klassningen KÖRDES och gav inget giltigt svar. Bedöm för hand.
 */
export type GapKlass =
  | 'dubblett'
  | 'kontexttapp'
  | 'kunskapslucka'
  | 'utanfor'
  | 'obedomd'
  | 'osorterad';

export interface GapBedomning {
  id: string;
  klass: GapKlass;
  motivering: string;
  sakerhet: 'hog' | 'medel' | 'lag';
  /** Bara för dubbletter: cosinuslikheten som avgjorde. */
  likhet?: number;
  dubblettAv?: {
    id: string;
    fraga: string;
    datum: string;
    kalla: string | null;
    status: string;
  };
  signaler: {
    ord: number;
    borjarMedSyftning: boolean;
    saknarEgennamn: boolean;
    harSvagTraff: boolean;
  };
  question_text: string;
  outcome: string;
  gaps_text: string | null;
  top_match_filename: string | null;
  top_match_similarity: number | null;
  created_at: string;
}

export interface GapTriageResult {
  bedomningar: GapBedomning[];
  total: number;
  /** Frågor som inte hann embeddas i den här omgången. > 0 → kör igen. */
  pendingEmbeddings: number;
  /**
   * Satt när minst en rad blev `obedomd`, med antal och orsak. Räknas på
   * utfallet, inte på anropen: ett svar som kapades efter sista posten har
   * bedömt allt och ska inte varna.
   */
  klassningsFel?: string | null;
}

export function triageGaps(): Promise<GapTriageResult> {
  return postJob<GapTriageResult>('/api/gap-triage', { action: 'triage' });
}

/** Utkast som täcker en eller flera luckor på samma gång. */
export function draftGapsFor(ids: string[]): Promise<GapDraft & { questionCount: number }> {
  return postJob('/api/gap-triage', { action: 'draft', ids });
}

export function commitGapsFor(
  ids: string[],
  filename: string,
  content: string,
): Promise<{ ok: true; filename: string; chunks: number; closed: number }> {
  return postJob('/api/gap-triage', { action: 'commit', ids, filename, content });
}

/**
 * Så många luckor EN källa får täcka. Speglar MAX_LUCKOR_PER_KALLA i
 * netlify/functions/gap-triage.ts, som avvisar fler — gränsen finns här bara
 * för att kunna säga ifrån innan användaren hunnit skriva ett helt utkast.
 */
export const MAX_LUCKOR_PER_KALLA = 20;

// ---------- Admin-operationer (kräver admin-email i ADMIN_EMAILS) ----------

export interface AdminGetResponse {
  filename: string;
  content: string;
  chunk_count: number;
  warning: string | null;
}

async function adminRequest<T>(body: object, token: string): Promise<T> {
  const res = await fetch('/api/admin-source-ops', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) message = b.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export async function adminGetSource(filename: string, token: string): Promise<AdminGetResponse> {
  return adminRequest<AdminGetResponse>({ action: 'get', filename }, token);
}

export async function adminUpdateSource(
  filename: string,
  content: string,
  token: string,
): Promise<{ ok: true; filename: string; chunks: number }> {
  return adminRequest({ action: 'update', filename, content }, token);
}

export async function adminDeleteSource(
  filename: string,
  token: string,
): Promise<{ ok: true; filename: string; chunks_removed: number }> {
  return adminRequest({ action: 'delete', filename }, token);
}

export async function adminSetDepartment(
  filename: string,
  department: string | null,
  token: string,
): Promise<{ ok: true; filename: string; department: string | null }> {
  return adminRequest({ action: 'set-department', filename, department }, token);
}

// ---------- Ledningsrapport ----------

/** En rad ur `lista`. Sökvägen och skickad_till returneras aldrig – bara om PDF:en finns. */
export interface LedningsrapportRad {
  manad: string;
  skapad_at: string;
  skickad_at: string | null;
  i_korthet: string | null;
  underlag: LedningsrapportUnderlag;
  har_pdf: boolean;
}

export type LedningsrapportBegaran =
  | { atgard: 'lista' }
  | { atgard: 'ladda_ner'; manad: string }
  | { atgard: 'skapa'; manad: string; skapa_om?: boolean }
  | { atgard: 'pdf'; manad: string }
  | { atgard: 'skicka'; manad: string; till?: 'mig' };

/**
 * POST /api/ledningsrapport. Svaret är åtgärdens JSON; fel blir ett Error
 * med läsbar text. Vid 5xx visas bara serverns `meddelande`, aldrig `error`.
 */
export async function ledningsrapport<T = Record<string, unknown>>(begaran: LedningsrapportBegaran): Promise<T> {
  const res = await fetch('/api/ledningsrapport', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await authHeader(),
    },
    body: JSON.stringify(begaran),
  });
  if (!res.ok) {
    // Servern skickar `meddelande` när texten är skriven för admin (t.ex. Resends 409
    // eller saknad mottagarlista) – den ska fram även vid 5xx.
    let meddelande: string | undefined;
    try {
      const m = ((await res.clone().json()) as { meddelande?: unknown }).meddelande;
      if (typeof m === 'string' && m) meddelande = m;
    } catch {
      /* inte JSON */
    }
    const text =
      meddelande ??
      (res.status === 408 || res.status === 504
        ? 'Ledningsrapporten tog för lång tid och avbröts. Försök igen om en stund.'
        : await felMeddelande(res, 'Ledningsrapporten'));
    throw Object.assign(new Error(text), { status: res.status });
  }
  return (await res.json()) as T;
}

// ---------- Möten ----------

/**
 * POST /api/moten – en åtgärd per anrop. Felet bär `status` (HTTP) och
 * `serverText` (serverns svenska text) så att anroparen kan skilja permanenta
 * fel från övergående.
 */
export async function motenApi<T>(body: { atgard: string } & Record<string, unknown>): Promise<T> {
  const res = await fetch('/api/moten', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await authHeader() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let text = '';
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) text = b.error;
    } catch {
      /* ingen JSON-kropp */
    }
    throw Object.assign(new Error(text || `HTTP ${res.status}`), { status: res.status, serverText: text });
  }
  return res.json() as Promise<T>;
}

/** En rad i möteslistan (`atgard: 'lista'`). Admin-rader utan åtkomst bär bara metadata. */
export interface MotesListrad {
  id: string;
  titel: string;
  typ: MotesTyp;
  kalla: Kalla;
  status: MotesStatus;
  fel: string | null;
  motesdatum: string | null;
  skapad_at: string;
  agare_epost: string | null;
  atkomst: 'allt' | 'metadata';
  relation: 'egen' | 'delad' | 'admin';
}

export interface MotesDeltagare {
  user_id: string;
  epost: string | null;
}

/** Svaret på `atgard: 'hamta'`. */
export interface MotesDetalj {
  mote: Mote;
  deltagare: MotesDeltagare[];
  kan: { andra: boolean; radera: boolean; lagg_i_kb: boolean };
  /** Är utskrift av filer (tal-till-text) påslagen i miljön? */
  stt_pa?: boolean;
  /** Är Klang-kopplingen (KLANG_API_KEY) påslagen? */
  klang_pa?: boolean;
}

/** Svaret på `atgard: 'lista'`. */
export interface MotesListsvar {
  moten: MotesListrad[];
  stt_pa?: boolean;
  klang_pa?: boolean;
  /** Möten är inte släppt och användaren är inte admin (MOTEN_SLAPPT). */
  kommer_snart?: boolean;
}
